import type { Ticket } from '../../types/ticket.types';

const localeNumericCompare = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** Matches getmanageticket view ordering: alpha prefix, then first numeric segment. */
const getSeatSortParts = (
  seatNo: string | number | null | undefined,
): [number, string, number, string] => {
  const str = String(seatNo ?? '').trim();
  const hasLeadingAlpha = /^[A-Za-z]/.test(str);
  const alphaPrefix = (str.match(/^[A-Za-z]+/)?.[0] ?? '').toLowerCase();
  const firstNumMatch = str.match(/\d+/);
  const firstNum = firstNumMatch ? Number.parseInt(firstNumMatch[0], 10) : Number.MAX_SAFE_INTEGER;
  const prefixGroup = hasLeadingAlpha ? 0 : 1;
  return [prefixGroup, alphaPrefix, firstNum, str.toLowerCase()];
};

export const compareSeatNumbers = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number => {
  const partsA = getSeatSortParts(a);
  const partsB = getSeatSortParts(b);
  for (let i = 0; i < partsA.length; i += 1) {
    const va = partsA[i];
    const vb = partsB[i];
    if (typeof va === 'number' && typeof vb === 'number') {
      if (va !== vb) return va - vb;
    } else if (va !== vb) {
      return localeNumericCompare(String(va), String(vb));
    }
  }
  return 0;
};

const compareRowValues = (a: string | null | undefined, b: string | null | undefined): number => {
  const aStr = String(a ?? '').trim();
  const bStr = String(b ?? '').trim();
  const aNum = Number.parseFloat(aStr);
  const bNum = Number.parseFloat(bStr);
  if (Number.isFinite(aNum) && Number.isFinite(bNum) && aStr === String(aNum) && bStr === String(bNum)) {
    return aNum - bNum;
  }
  return localeNumericCompare(aStr, bStr);
};

const getTicketActivityTs = (ticket: Ticket): number =>
  Date.parse(String(ticket.tp_updated_at ?? '')) || 0;

const getEventKey = (ticket: Ticket): string =>
  String(ticket.e_id ?? `${ticket.e_name ?? ''}|${ticket.e_date ?? ''}`);

/** Latest add/update time across all tickets for the same event. */
const buildEventActivityMap = (tickets: Ticket[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const ticket of tickets) {
    const key = getEventKey(ticket);
    const ts = getTicketActivityTs(ticket);
    map.set(key, Math.max(map.get(key) ?? 0, ts));
  }
  return map;
};

const compareTicketsByLocation = (a: Ticket, b: Ticket): number => {
  const dateA = String(a.e_date ?? '');
  const dateB = String(b.e_date ?? '');
  if (dateA !== dateB) return dateA.localeCompare(dateB);

  const eventCmp = localeNumericCompare(String(a.e_name ?? ''), String(b.e_name ?? ''));
  if (eventCmp !== 0) return eventCmp;

  const sectionCmp = localeNumericCompare(String(a.tp_section ?? ''), String(b.tp_section ?? ''));
  if (sectionCmp !== 0) return sectionCmp;

  const rowCmp = compareRowValues(a.tp_row, b.tp_row);
  if (rowCmp !== 0) return rowCmp;

  const seatCmp = compareSeatNumbers(a.tp_seat_no, b.tp_seat_no);
  if (seatCmp !== 0) return seatCmp;

  return String(a.tp_id ?? '').localeCompare(String(b.tp_id ?? ''));
};

/**
 * Events with the most recent add/update appear first.
 * Within each event, tickets stay in section → row → seat order.
 */
export const sortManageTickets = (tickets: Ticket[]): Ticket[] => {
  if (tickets.length <= 1) return [...tickets];

  const eventActivity = buildEventActivityMap(tickets);

  return [...tickets].sort((a, b) => {
    const keyA = getEventKey(a);
    const keyB = getEventKey(b);
    const activityA = eventActivity.get(keyA) ?? 0;
    const activityB = eventActivity.get(keyB) ?? 0;

    if (activityA !== activityB) return activityB - activityA;

    if (keyA !== keyB) return compareTicketsByLocation(a, b);

    const sectionCmp = localeNumericCompare(String(a.tp_section ?? ''), String(b.tp_section ?? ''));
    if (sectionCmp !== 0) return sectionCmp;

    const rowCmp = compareRowValues(a.tp_row, b.tp_row);
    if (rowCmp !== 0) return rowCmp;

    const seatCmp = compareSeatNumbers(a.tp_seat_no, b.tp_seat_no);
    if (seatCmp !== 0) return seatCmp;

    return String(a.tp_id ?? '').localeCompare(String(b.tp_id ?? ''));
  });
};

export const MANAGE_TICKETS_ORDER_BY = [
  { tp_updated_at: 'desc' },
  { e_date: 'asc' },
  { e_name: 'asc' },
  { tp_section: 'asc' },
  { tp_row: 'asc' },
  { tp_seat_no: 'asc' },
  { tp_id: 'asc' },
];

export const getManageTicketsTrackingVariables = (
  searchEvent = '%',
  searchClient = '%',
) => ({
  pageSize: 5000,
  pageOffset: 0,
  search_event: searchEvent,
  search_client: searchClient,
  order_by: MANAGE_TICKETS_ORDER_BY,
});

export type UserDetailTicketSortFields = {
  tpId?: string;
  eventId?: string;
  event?: string;
  eventDateRaw?: string;
  updatedAt?: string;
  section?: string;
  row?: string;
  seatNo?: string;
};

const normalizeUserDetailField = (value: string | undefined): string => {
  const str = String(value ?? '').trim();
  return str === '-' ? '' : str;
};

/** Same event-activity + section/row/seat ordering as Manage Tickets, for User Details rows. */
export const sortUserDetailTickets = <T extends UserDetailTicketSortFields>(tickets: T[]): T[] => {
  if (tickets.length <= 1) return [...tickets];

  const getEventKey = (ticket: T): string =>
    String(ticket.eventId ?? `${ticket.event ?? ''}|${ticket.eventDateRaw ?? ''}`);

  const getActivityTs = (ticket: T): number =>
    Date.parse(String(ticket.updatedAt ?? '')) || 0;

  const eventActivity = new Map<string, number>();
  for (const ticket of tickets) {
    const key = getEventKey(ticket);
    const ts = getActivityTs(ticket);
    eventActivity.set(key, Math.max(eventActivity.get(key) ?? 0, ts));
  }

  return [...tickets].sort((a, b) => {
    const keyA = getEventKey(a);
    const keyB = getEventKey(b);
    const activityA = eventActivity.get(keyA) ?? 0;
    const activityB = eventActivity.get(keyB) ?? 0;

    if (activityA !== activityB) return activityB - activityA;

    if (keyA !== keyB) {
      const dateA = String(a.eventDateRaw ?? '');
      const dateB = String(b.eventDateRaw ?? '');
      if (dateA !== dateB) return dateA.localeCompare(dateB);

      const eventCmp = localeNumericCompare(String(a.event ?? ''), String(b.event ?? ''));
      if (eventCmp !== 0) return eventCmp;

      const sectionCmp = localeNumericCompare(
        normalizeUserDetailField(a.section),
        normalizeUserDetailField(b.section),
      );
      if (sectionCmp !== 0) return sectionCmp;

      const rowCmp = compareRowValues(
        normalizeUserDetailField(a.row),
        normalizeUserDetailField(b.row),
      );
      if (rowCmp !== 0) return rowCmp;

      return compareSeatNumbers(
        normalizeUserDetailField(a.seatNo),
        normalizeUserDetailField(b.seatNo),
      );
    }

    const sectionCmp = localeNumericCompare(
      normalizeUserDetailField(a.section),
      normalizeUserDetailField(b.section),
    );
    if (sectionCmp !== 0) return sectionCmp;

    const rowCmp = compareRowValues(
      normalizeUserDetailField(a.row),
      normalizeUserDetailField(b.row),
    );
    if (rowCmp !== 0) return rowCmp;

    const seatCmp = compareSeatNumbers(
      normalizeUserDetailField(a.seatNo),
      normalizeUserDetailField(b.seatNo),
    );
    if (seatCmp !== 0) return seatCmp;

    return String(a.tpId ?? '').localeCompare(String(b.tpId ?? ''));
  });
};

export const parseAndSortSeatNumbers = (seatNo: string | number | null | undefined): string[] => {
  if (!seatNo) return ['1'];
  const seatNoStr = String(seatNo);
  let seats: string[];

  if (seatNoStr.includes('-')) {
    const [start, end] = seatNoStr.split('-').map(Number);
    if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
      seats = Array.from({ length: end - start + 1 }, (_, i) => (start + i).toString());
    } else {
      seats = [seatNoStr];
    }
  } else if (seatNoStr.includes(',')) {
    seats = seatNoStr.split(',').map((s) => s.trim()).filter(Boolean);
  } else {
    seats = [seatNoStr];
  }

  return [...seats].sort(compareSeatNumbers);
};
